import React, { useState } from "react";
import { Plus, Trash2, Pencil } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";

function CrudList({ title, items, endpoint, reload, hasRank }) {
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", description: "", rank: 99 });

  const openCreate = () => { setEditing(null); setForm({ name: "", description: "", rank: (items.length || 0) + 1 }); setOpen(true); };
  const openEdit = (it) => { setEditing(it); setForm({ name: it.name, description: it.description || "", rank: it.rank || 99 }); setOpen(true); };

  const save = async () => {
    try {
      if (editing) await api.put(`${endpoint}/${editing.id}`, form);
      else await api.post(endpoint, form);
      toast.success("Saved"); setOpen(false); reload();
    } catch { toast.error("Save failed"); }
  };
  const remove = async (it) => { await api.delete(`${endpoint}/${it.id}`); toast.success("Deleted"); reload(); };

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-5">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-semibold text-slate-800">{title}</h3>
        <Button size="sm" variant="outline" onClick={openCreate} data-testid={`add-${title.replace(/\s/g, "-").toLowerCase()}`}><Plus className="w-4 h-4 mr-1" /> Add</Button>
      </div>
      <div className="space-y-2">
        {items.map((it) => (
          <div key={it.id} className="flex items-center gap-3 rounded-lg border border-slate-100 p-3">
            {hasRank && <span className="w-6 h-6 rounded bg-slate-100 text-xs flex items-center justify-center text-slate-500">{it.rank}</span>}
            <div className="min-w-0 flex-1"><p className="text-sm font-medium text-slate-800">{it.name}</p>{it.description && <p className="text-xs text-slate-400">{it.description}</p>}</div>
            <Button size="icon" variant="ghost" onClick={() => openEdit(it)}><Pencil className="w-4 h-4" /></Button>
            <Button size="icon" variant="ghost" className="text-rose-500" onClick={() => remove(it)}><Trash2 className="w-4 h-4" /></Button>
          </div>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>{editing ? "Edit" : "Add"} {title.replace(/s$/, "")}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1" data-testid="ref-form-name" /></div>
            {hasRank
              ? <div><Label>Rank</Label><Input type="number" value={form.rank} onChange={(e) => setForm({ ...form, rank: parseInt(e.target.value) || 99 })} className="mt-1" /></div>
              : <div><Label>Description</Label><Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="mt-1" /></div>}
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button><Button onClick={save} data-testid="ref-form-save">Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default function AdminRoles({ refs }) {
  return (
    <div className="grid md:grid-cols-2 gap-6">
      <CrudList title="Role Categories" items={refs.roleCategories} endpoint="/role-categories" reload={refs.reload} />
      <CrudList title="Role Levels" items={refs.roleLevels} endpoint="/role-levels" reload={refs.reload} hasRank />
    </div>
  );
}
